import { create } from 'zustand';

import { get } from '@/lib/api';

/**
 * The guided tour's state (§11.2).
 *
 * Zustand rather than component state because the tour outlives any one screen:
 * a step can send the reader to another route, and the overlay has to survive
 * that navigation and carry on from where it was. State held in the page being
 * navigated away from would end the tour at its second step.
 */

export interface TourStep {
    key: string;
    /**
     * The `data-tour` key of the element to spotlight. Null means a centred
     * card, for the steps that are about the platform rather than a control.
     */
    target: string | null;
    placement: 'center' | 'end' | 'start' | 'top' | 'bottom';
    /**
     * A screen to open before the step runs. This is what makes it a tour of
     * the platform rather than a tour of the menu.
     */
    route: string | null;
    title_en: string;
    title_ar: string;
    body_en: string;
    body_ar: string;
}

export interface Tour {
    role: string;
    /** Bumped when the tour changes materially, so it can be offered again. */
    version: number;
    steps: TourStep[];
}

/**
 * What has been seen, and which version of it.
 *
 * In the browser rather than on the server: whether somebody has watched an
 * onboarding animation is not a fact about the Bank's customers, and putting it
 * in the database would invite a report on who has not finished it.
 */
const SEEN_KEY = 'dbcx.tour.seen';

function seenVersion(): number {
    try {
        const raw = window.localStorage.getItem(SEEN_KEY);

        return raw === null ? 0 : Number.parseInt(raw, 10) || 0;
    } catch {
        // No storage: treat the tour as seen rather than running it on every
        // single page load, which would be far worse than never running it.
        return Number.MAX_SAFE_INTEGER;
    }
}

function markSeen(version: number): void {
    try {
        window.localStorage.setItem(SEEN_KEY, String(version));
    } catch {
        // It may be offered again next time. Acceptable.
    }
}

interface TourState {
    tour: Tour | null;
    isActive: boolean;
    index: number;
    /** True while a step is waiting for its target to appear after a move. */
    isSettling: boolean;

    /** Fetch the tour and start it only if this person has not seen it. */
    offer: () => Promise<void>;
    /** Start it regardless — from the account menu, or the guide. */
    start: () => Promise<void>;
    next: () => void;
    back: () => void;
    goTo: (index: number) => void;
    finish: () => void;
    dismiss: () => void;
    setSettling: (settling: boolean) => void;
}

export const useTourStore = create<TourState>((set, getState) => ({
    tour: null,
    isActive: false,
    index: 0,
    isSettling: false,

    offer: async () => {
        if (getState().isActive) {
            return;
        }

        try {
            const response = await get<{ data: Tour }>('/tour');
            const tour = response.data;

            if (tour.steps.length === 0 || seenVersion() >= tour.version) {
                // Already seen this version, or there is nothing to show.
                set({ tour });

                return;
            }

            set({ tour, isActive: true, index: 0 });
        } catch {
            /*
             * A tour that cannot load is not an error worth showing anybody. The
             * platform is perfectly usable without it, and an error toast on the
             * first screen after sign-in would be a worse first impression than
             * a missing animation.
             */
        }
    },

    start: async () => {
        const existing = getState().tour;

        if (existing !== null && existing.steps.length > 0) {
            set({ isActive: true, index: 0 });

            return;
        }

        try {
            const response = await get<{ data: Tour }>('/tour');

            if (response.data.steps.length > 0) {
                set({ tour: response.data, isActive: true, index: 0 });
            }
        } catch {
            // As above.
        }
    },

    next: () => {
        const { tour, index } = getState();

        if (tour === null) {
            return;
        }

        if (index >= tour.steps.length - 1) {
            getState().finish();

            return;
        }

        set({ index: index + 1 });
    },

    back: () => {
        const { index } = getState();

        set({ index: Math.max(0, index - 1) });
    },

    goTo: (index: number) => {
        const { tour } = getState();

        if (tour === null) {
            return;
        }

        set({ index: Math.min(Math.max(0, index), tour.steps.length - 1) });
    },

    finish: () => {
        const { tour } = getState();

        if (tour !== null) {
            markSeen(tour.version);
        }

        set({ isActive: false, index: 0 });
    },

    /**
     * Left early — which counts as seen.
     *
     * Somebody who closed the tour has told us something; showing it again
     * tomorrow would be ignoring them, and would teach them to close the next
     * thing the platform says too.
     */
    dismiss: () => {
        getState().finish();
    },

    setSettling: (settling: boolean) => set({ isSettling: settling }),
}));

/** For tests and for the "show me again" control. */
export function resetTourSeen(): void {
    try {
        window.localStorage.removeItem(SEEN_KEY);
    } catch {
        // Nothing to reset.
    }
}
